import { language } from '@/common/storage'
import {
  GameDifficultyItem,
  GameDifficultyCategoryId,
} from '@/types/dmaker/game-config.type'
import {
  categoryConfig,
  categoryPrefix,
  gameDifficultyCategoryConfig,
  gameDifficultyCategoryList,
  globalConfigCategoryResourceType,
  i18nCategoryResourceType,
  is3DMaterialCategory,
  is3DTextureCategory,
  isCameraCategory,
  isFePresetCategory,
  isPresetOptionCategory,
  threeDimensionalMaterialResourceType,
  threeDimensionalModelTextureResourceType,
} from '@/store/creative/common/category'
import { resourceConfig } from '@/store/creative/common/resource'
import { Resource } from '@/types/dmaker/resource.type'
import { CookedCategory } from '@/types/dmaker/category.type'
import { getFileExtension } from '@com/utils/file'
import _ from 'lodash'

export function isZip(type: string): boolean {
  return type === 'zip'
}

export function isGlobalConfigCategory(type: string): boolean {
  return type === globalConfigCategoryResourceType
}

export function isBgColorCategory(type: string): boolean {
  return type === categoryPrefix.bgColor
}

export function isFrameSequence(prefix: string, resourceType: string): boolean {
  let finalPrefix = prefix
  // 如果含有 :，则取最后一个，兼容预设资源
  if (prefix.includes(':')) {
    finalPrefix = prefix.split(':')[prefix.split(':').length - 1]!
  }
  return (
    isZip(resourceType) &&
    new RegExp(`^${resourceConfig.frameSequence.prefix}`).test(finalPrefix)
  )
}

export function isDragonBones(prefix: string): boolean {
  let finalPrefix = prefix
  // 如果含有 :，则取最后一个，兼容预设资源
  if (prefix.includes(':')) {
    finalPrefix = prefix.split(':')[prefix.split(':').length - 1]!
  }
  return new RegExp(`^${resourceConfig.dragonBones.prefix}`).test(finalPrefix)
}

export function isAnimationPrefix(prefix: string): boolean {
  return prefix.includes('@')
}

export function isModelResource(resource: Resource): boolean {
  return ['3DModel', '3DModelCocos'].some(
    (val) => val === resource.sub_resource_type,
  )
}

export function isCocosModelResource(resource: Resource): boolean {
  return (
    resource.sub_resource_type === '3DModelCocos' &&
    !isAnimationPrefix(resource.prefix)
  )
}

export function isCocosModelCategory(category: CookedCategory): boolean {
  return (
    category.sub_resource_type === '3DModelCocos' &&
    !isAnimationPrefix(category.prefix)
  )
}

export function isCocosModelAnimationCategory(
  category: CookedCategory,
): boolean {
  return (
    category.sub_resource_type === '3DModelCocos' &&
    isAnimationPrefix(category.prefix)
  )
}

export function isCocosModelAnimationResource(resource: Resource): boolean {
  return (
    resource.sub_resource_type === '3DModelCocos' &&
    isAnimationPrefix(resource.prefix)
  )
}

export function isModelCategory(
  id: number,
  resource: { [id: number]: Resource[] },
): boolean {
  // @ts-ignore
  return (resource[id] && isModelResource(resource[id][0])) || false
}

export function isTextureResource(resource: Resource): boolean {
  return resource.sub_resource_type === threeDimensionalModelTextureResourceType
}

export function is3DMaterialResource(resouce: Resource): boolean {
  return resouce.sub_resource_type === threeDimensionalMaterialResourceType
}

export function isTexturePrefix(prefix: string): boolean {
  const texturePrefix = [
    '_color',
    '_normal',
    '_pbr',
    '_emissive',
    '_occlusion',
    '_hsvMask',
  ]
  return texturePrefix.some((s) => prefix.includes(s))
}

export function getTargetTextureResourceIndex(
  resource: Resource,
  prefix: string,
): boolean {
  const textureNormsList = [
    `${prefix}_color`,
    `${prefix}_normal`,
    `${prefix}_pbr`,
    `${prefix}_emissive`,
    `${prefix}_occlusion`,
    `${prefix}_hsvMask`,
  ]
  return textureNormsList.indexOf(resource.prefix)
}

/**
 * 获取3DCocosModel的贴图
 * resource: state.creative.template.templateConfig.resource
 */
export function get3dModelTextureCategoryList(
  resource: Resource,
  prefix: CookedCategory['prefix'],
): CookedCategory[] {
  const subCategoryList: CookedCategory[] = []
  const textureNormsList = [
    `${prefix}_color`,
    `${prefix}_normal`,
    `${prefix}_pbr`,
    `${prefix}_emissive`,
    `${prefix}_occlusion`,
    `${prefix}_hsvMask`,
  ]
  _.each(resource, (res, key) => {
    // @ts-ignore
    res.forEach((item) => {
      if (
        isTextureResource(item) &&
        textureNormsList.indexOf(item.prefix) !== -1
      ) {
        subCategoryList.push(item)
      }
    })
  })
  return subCategoryList
}

export function getDragonBonesImage(urls: string[]): string {
  if (Array.isArray(urls)) {
    return (
      urls.find((url) => {
        return /\.(png|jpe?g)$/.test(url)
      }) || ''
    )
  }
  return ''
}

export const isGamePlay = (id: number): boolean =>
  id === categoryConfig.gamePlay!.id

export const isElement = (id: number): boolean =>
  id === categoryConfig.element!.id

// 一级分类的 id 是固定的
export const isExpandedContent = (id: number): boolean =>
  id === categoryConfig.expandedContent!.id

export const isElementStars = (id: number): boolean =>
  id === categoryConfig.elementStars!.id

export const isGameConfigStars = (id: number): boolean =>
  id === categoryConfig.gameConfigStars!.id

export const isPlayAgain = (id: number): boolean =>
  id === categoryConfig.playAgain!.id

export const isAutoEndTime = (id: number): boolean =>
  id === categoryConfig.autoEndTime!.id

// 全局可点
export const isGlobalClickCategory = (id: number): boolean =>
  id === categoryConfig.globalClick!.id
// 诱导点击
export const isYdClickCategory = (id: number): boolean =>
  id === categoryConfig.ydClick!.id
// 自动跳转
export const isAutoClickCategory = (id: number): boolean =>
  id === categoryConfig.autoClick!.id

export const isColorConfig = (id: number): boolean =>
  id === categoryConfig.colorConfig!.id

export const isSceneList = (id: number): boolean =>
  id === categoryConfig.scene!.id

// 是否是游戏难度分类
export const isMultiGameDifficulty = (prefix: string): boolean => {
  return Object.values(gameDifficultyCategoryConfig)
    .map((item) => item.prefix)
    .includes(prefix)
}

// 是否是多组合元素分类
export const isMultiCustomCategory = (id: number): boolean =>
  id === categoryConfig.multiCustomCategory!.id

// 图片语言包大分类
export const isImageLangPack = ({
  prefix,
  resourceType,
}: {
  prefix: string
  resourceType: string
}): boolean => {
  return isZip(resourceType) && prefix === 'lang'
}

export const isLangPack = ({
  prefix,
  resourceType,
}: {
  prefix: string
  resourceType: string
}): boolean => {
  // 代码字语言大分类
  if (resourceType === 'json' && prefix === 'i18n') return true
  // 图片语言包大分类
  if (isZip(resourceType) && prefix === 'lang') return true
  return false
}

/**
 * 判断是否是语言分类，包括代码字语言大分类、图片语言包大分类、代码字分类
 */
export const isLang = ({
  prefix,
  resourceType,
}: {
  prefix: string
  resourceType: string
}): boolean => {
  // 代码字语言大分类
  if (resourceType === 'json' && prefix === 'i18n') return true
  // 图片语言包大分类
  if (
    isImageLangPack({
      prefix,
      resourceType,
    })
  ) {
    return true
  }
  // 代码字分类
  if (resourceType === i18nCategoryResourceType) return true
  return false
}

/**
 * 没有关联数据的玩法
 * 有 resource/stage/group 三个属性之一，返回 false（不展示在 玩法 tab 下）
 * scene 场景、stage 环节、resource 资源、group 组
 */
export function isGameConfigWithoutRelate(data: GameDifficultyItem): boolean {
  return !data.resource && !data.stage && !data.group && !data.scene
}

export function isGameDifficultyCategory(
  id: GameDifficultyCategoryId,
): boolean {
  return gameDifficultyCategoryList.some((item) => item === id)
}

/**
 * 获取玩法分类名字
 */
export const getCategoryName = (category: CookedCategory): string => {
  return language.get() === 'en'
    ? category.name_en || category.name
    : category.name
}

// dco 制作
export const isDcoPage = (page: string): boolean => {
  return page === 'dco' || page === 'common-template-dco'
}

// 制作页面
export const isCustomAndMultiCustomPage = (page: string): boolean => {
  return [
    'custom',
    'multi-custom',
    'common-template-custom',
    'common-template-multi-custom',
  ].some((item) => item === page)
}

// 多组合制作
export const isMultiCustomPage = (page: string): boolean => {
  return page === 'multi-custom' || page === 'common-template-multi-custom'
}

// 通用模板制作页面(通用模板的制作页面使用相同的接口)，包括普通/dco/多组合制作方式
export const isCommonTemplateCustomPage = (page: string): boolean => {
  return [
    'common-template-custom',
    'common-template-dco',
    'common-template-multi-custom',
  ].some((item) => item === page)
}

export const isTemplatePage = (page: string): boolean => {
  return ['template', 'common-template'].some((item) => item === page)
}

export const isCommonTemplatePage = (page: string): boolean => {
  return page === 'common-template'
}

export const isCreativeTemplateCustomPage = (page: string): boolean => {
  return page === 'custom'
}

// 获取模型zip文件的文件列表类型
export const getFileListFileType = (fileUrls: string[]): string[] => {
  return fileUrls.map((url) => String(getFileExtension(url)).toLowerCase())
}

// 如果是预设的资源，拼接的 filePath 是预设目录下的，非预设资源都在 resource 目录下
export const fillFilePath = (filePath: string, isPreset: boolean): string => {
  return isPreset ? `presets/${filePath}` : `resource/${filePath}`
}

/**
 * 是否是平台展示需要隐藏的分类信息
 * 1. 材质、动画默认全部隐藏
 * 2. 贴图资源非定制中隐藏
 * 3. 预设分类下， 非镜头和预设 config 的坑位都隐藏
 * @param category 分类信息
 * @param isCreativeTemplateCustom 是否是定制创意模板
 */
export const isHiddenCategory = (
  category: CookedCategory,
  isCreativeTemplateCustom: boolean,
): boolean => {
  if (is3DMaterialCategory(category)) {
    return true
  }
  if (isCocosModelAnimationCategory(category)) {
    return true
  }
  if (is3DTextureCategory(category) && !isCreativeTemplateCustom) {
    return true
  }
  if (
    categoryConfig.preset.id === category.pid &&
    !(isCameraCategory(category) || isFePresetCategory(category))
  ) {
    return true
  }
  return false
}

/**
 * 获取模型的 json 文件路径
 * @param file_paths
 */
export const getJsonModelPath = (file_paths) => {
  const paths = file_paths.split(',')
  const jsonPath = paths.find((path) => path.includes('.json'))
  if (!jsonPath) return ''

  const nameIndex = jsonPath.lastIndexOf('/')
  if (nameIndex === -1) return ''

  return jsonPath.substring(0, nameIndex)
}
